import React, { useMemo, useState } from 'react';
import L from 'patches/leaflet';
import ReactDOMServer from 'react-dom/server';

import MissionControlMapView from './MissionControlMap.view';

import classes from './MissionControlMap.module.scss';
import evStations from 'assets/ev-stations.json';
import { ILatLng } from 'models/latlng';
import { IMMapMarker } from 'views/MMap/MMap.model';
import { IGetChargingPointResponse, IGetChargingPointsByIdResponse } from 'models/http/chargingPoint';
import { useHttp } from 'utils/hooks';
import { LayersTypes } from 'components/MapLayers/MapLayers.model';
import ChargingPointPopup from 'components/MapLayers/MapLayersPopup/Ev/ChargingPointPopup/ChargingPointPopup';
import ChargingPointIcon from 'components/MapLayers/MapLayersOptions/MapLayersIcons/ChargingPointIcon/ChargingPointIcon';

type Props = {
  chargingPoints: IGetChargingPointResponse['records'];
  showLayerType: LayersTypes | null;
};

const MissionControlMap: React.FC<Props> = (props: React.PropsWithChildren<Props>) => {
  const [singleChargingPointState, setSingleChargingPointState] = useState<(IGetChargingPointsByIdResponse & { id: number }) | null>(null);

  const http = useHttp();

  const eventHandlers = (id: number): L.LeafletEventHandlerFnMap | undefined => {
    return {
      click: () => {
        http.chargingPoint
          .getById(id)
          .then((response: IGetChargingPointsByIdResponse) => {
            setSingleChargingPointState(() => {
              return { ...response, id };
            });
          })
          .catch((error) => console.error(error));
      },
    };
  };

  const evMarkers: IMMapMarker[] = useMemo(() => {
    if (props.showLayerType !== LayersTypes.EV) {
      return [];
    }

    return props.chargingPoints['records'].map((chargingPoint: IGetChargingPointResponse['records'][number]) => {
      return {
        id: chargingPoint.id.toString(),
        coord: { lat: chargingPoint.coords.lat, lng: chargingPoint.coords.lng },
        popup: <ChargingPointPopup chargingPoint={singleChargingPointState}></ChargingPointPopup>,
        eventHandlers: eventHandlers(chargingPoint.id),
        icon: L.divIcon({
          className: classes['evIconContainer'],
          html: ReactDOMServer.renderToStaticMarkup(
            <ChargingPointIcon
              isChosen={singleChargingPointState?.id === chargingPoint.id}
              status={chargingPoint.status}
              mostConnectorPowerType={chargingPoint.mostConnectorPowerType}
              id={chargingPoint.id}
            ></ChargingPointIcon>,
          ),
        }),
      };
    });
  }, [props.showLayerType, props.chargingPoints, singleChargingPointState]);

  const demandCoords = useMemo(() => {
    return evStations.reduce((acc: { lat: number; lng: number }[], coord: ILatLng) => {
      return [
        ...acc,
        { lat: coord.lat - Number((Math.random() * 2).toFixed(2)), lng: coord.lng - Number((Math.random() * 2).toFixed(2)) },
        { lat: coord.lat - Number((Math.random() * 2).toFixed(2)), lng: coord.lng - Number((Math.random() * 2).toFixed(2)) },
        { lat: coord.lat - Number((Math.random() * 2).toFixed(2)), lng: coord.lng - Number((Math.random() * 2).toFixed(2)) },
        { lat: coord.lat - Number((Math.random() * 2).toFixed(2)), lng: coord.lng - Number((Math.random() * 2).toFixed(2)) },
        { lat: coord.lat - Number((Math.random() * 2).toFixed(2)), lng: coord.lng - Number((Math.random() * 2).toFixed(2)) },
      ];
    }, []);
  }, []);

  const dots: IMMapMarker[] = useMemo(() => {
    if (props.showLayerType !== LayersTypes.Booking) {
      return [];
    }

    return demandCoords.map((coord: { lat: number; lng: number }) => {
      return {
        coord,
        icon: L.divIcon({
          className: classes['dotIconContainer'],
          html: ReactDOMServer.renderToStaticMarkup(<div className={classes['dotIconContent']}></div>),
        }),
      };
    });
  }, [props.showLayerType]);

  const heatmap: IMMapMarker[] = useMemo(() => {
    if (props.showLayerType !== LayersTypes.Heat) {
      return [];
    }

    return demandCoords.map((coord: { lat: number; lng: number }) => {
      return {
        coord,
        icon: L.divIcon({
          iconAnchor: [25, 25],
          className: classes['heatmapIconContainer'],
          html: ReactDOMServer.renderToStaticMarkup(<div className={classes['heatmapIconContent']}></div>),
        }),
      };
    });
  }, [props.showLayerType]);

  return <MissionControlMapView showLayerType={props.showLayerType} markers={[...dots, ...evMarkers, ...heatmap]}></MissionControlMapView>;
};

MissionControlMap.displayName = 'MissionControlMap';

export default MissionControlMap;
